// /src/renderer/features/browser/BrowserPage.tsx
import React, { useCallback, useEffect, useMemo, useState } from 'react';
// import './BrowserPage.scss';
import styles from './BrowserPage.module.scss';
import BrowserList from './BrowserList/BrowserList';
import BrowserHeader from './BrowserHeader/BrowserHeader';
import BrowserFooter from './BrowserFooter/BrowserFooter';
import useStudyGroup from '@hooks/study/useStudyGroup';
import { useBrowserPage } from './useBrowserPage';
import { StudyFilter } from 'config/studyConfig';
import SimpleContextMenu, {
  SimpleContextMenuItem,
} from '@components/ContextMenu/SimpleContextMenu';
import { BROWSER_COLUMNS } from 'config/tableColumns';
import { Option } from '@components/MultiSelectDropdown/MultiSelectDropdown';

// ✅ 컬럼 prefs: 순서(order) + 숨김(hidden)
type ColumnPrefs = {
  order: string[];
  hidden: string[];
};

const PREFS_KEY = 'browser.columns.prefs.v1';
const LEGACY_VISIBLE_KEY = 'browser.visibleColumns';

function loadPrefs(availableIds: string[]): ColumnPrefs {
  try {
    // 1) 새 포맷
    const raw = localStorage.getItem(PREFS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<ColumnPrefs>;
      const orderRaw = Array.isArray(parsed.order) ? parsed.order : availableIds;
      const hiddenRaw = Array.isArray(parsed.hidden) ? parsed.hidden : [];

      // normalize (추가/삭제 컬럼 대응)
      const orderClean = orderRaw.filter((id) => availableIds.includes(id));
      const missing = availableIds.filter((id) => !orderClean.includes(id));
      const order = [...orderClean, ...missing];

      const hidden = hiddenRaw.filter((id) => availableIds.includes(id));
      return { order, hidden };
    }

    // 2) 예전 포맷(visibleColumnIds)에서 마이그레이션
    const legacy = localStorage.getItem(LEGACY_VISIBLE_KEY);
    if (legacy) {
      const visibleIds = JSON.parse(legacy) as string[];
      const visibleSet = new Set(Array.isArray(visibleIds) ? visibleIds : []);
      const hidden = availableIds.filter((id) => !visibleSet.has(id));
      return { order: availableIds, hidden };
    }
  } catch {
    // ignore
  }

  return { order: availableIds, hidden: [] };
}

const BrowserPage: React.FC = (): React.JSX.Element => {
  // 공통
  const [Key, setKey] = useState(0);
  const {
    resetStudyViewState,
    StudyViewState,
    updateFilters,
    updateStudies,
    updateSortOptions,
  } = useBrowserPage();
  // const { general } = useSettingsModal();

  const { study, error, refetch, isLoading } = useStudyGroup({
    page: StudyViewState.currentPage,
    limit: StudyViewState.perPage,
    filters: StudyViewState.filters,
    sort: StudyViewState.sortOption,
  });

  const { data: studyData = [] } = study || {};

  const totalPages = useMemo(() => {
    const count = Number(study?.count ?? 0);
    return Math.ceil(count / StudyViewState.perPage);
  }, [study?.count, StudyViewState.perPage]);

  //Header 함수
  const handleClickRefresh = () => {
    resetStudyViewState();
    refetch();
    setKey((prev) => prev + 1);
  };

  const handleClickRefreshKeepPage = useCallback(() => {
    refetch();
  }, [refetch]);

  const handleClickQuery = (filters: Partial<StudyFilter>) => {
    updateFilters(filters);
    updateSortOptions({});
    refetch();
  };

  useEffect(() => {
    if (studyData.length > 0) {
      updateStudies(studyData);
    }
  }, [studyData, updateStudies]);

  // =========================
  // ✅ Columns: 단일 소스 = prefs(order+hidden)
  // =========================
  const availableIds = useMemo(() => BROWSER_COLUMNS.map((c) => c.id), []);

  const [prefs, setPrefs] = useState<ColumnPrefs>(() => loadPrefs(availableIds));
  const [isColumnsMenuOpen, setIsColumnsMenuOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    // 예전 키는 남겨도 무방하지만, 원하면 제거 가능
    // localStorage.removeItem(LEGACY_VISIBLE_KEY);
  }, [prefs]);

  const hiddenSet = useMemo(() => new Set(prefs.hidden), [prefs.hidden]);

  const columnsById = useMemo(() => {
    return new Map(BROWSER_COLUMNS.map((c) => [c.id, c]));
  }, []);

  const orderedColumns = useMemo(() => {
    return prefs.order
      .map((id) => columnsById.get(id))
      .filter(Boolean) as (typeof BROWSER_COLUMNS)[number][];
  }, [prefs.order, columnsById]);


  const { visibleColumns, columnOptions } = useMemo(() => {
    const ordered = prefs.order
      .map((id) => columnsById.get(id))
      .filter(Boolean) as (typeof BROWSER_COLUMNS)[number][];

    return {
      visibleColumns: ordered.filter((c) => !hiddenSet.has(c.id)),
      columnOptions: ordered.map((col) => ({
        value: col.id,
        label: col.label,
        isSelected: !hiddenSet.has(col.id),
      })),
    };
  }, [prefs.order, columnsById, hiddenSet]);

  const handleChangeColumns = useCallback((options: Option[]) => {
    const selectedIds = new Set(
      options
        .filter((option) => option.isSelected)
        .map((option) => String(option.value)),
    );
    if (selectedIds.size === 0) return;

    setPrefs((prev) => ({
      ...prev,
      hidden: prev.order.filter((id) => !selectedIds.has(id)),
    }));
  }, []);

  const toggleColumn = useCallback((id: string) => {
    setPrefs((prev) => {
      const hidden = new Set(prev.hidden);

      // 최소 1개 컬럼은 남기기
      const visibleCount = prev.order.filter((cid) => !hidden.has(cid)).length;

      if (hidden.has(id)) {
        hidden.delete(id); // show
      } else {
        if (visibleCount <= 1) return prev;
        hidden.add(id); // hide
      }

      return { ...prev, hidden: Array.from(hidden) };
    });
  }, []);

  const columnMenuItems: SimpleContextMenuItem[] = useMemo(() => {
    // ✅ 메뉴는 "현재 order"대로 보여줌 (드래그 순서 유지)
    return orderedColumns.map((col) => ({
      label: col.label,
      isChecked: !hiddenSet.has(col.id),
      onClick: () => toggleColumn(col.id),
    }));
  }, [orderedColumns, hiddenSet, toggleColumn]);

  const handleClickColumns = (e: React.MouseEvent) => {
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    // lastMouseClick.x = Math.round(rect.right);
    // lastMouseClick.y = Math.round(rect.bottom);

    e.preventDefault();
    e.stopPropagation();
    setIsColumnsMenuOpen(true);
  };

  // ✅ 드래그로 컬럼 순서 변경 (order 전체에 적용: hidden 포함)
  const moveColumn = useCallback((fromId: string, toId: string) => {
    if (fromId === toId) return;

    setPrefs((prev) => {
      const nextOrder = [...prev.order];
      const fromIndex = nextOrder.indexOf(fromId);
      const toIndex = nextOrder.indexOf(toId);
      if (fromIndex === -1 || toIndex === -1) return prev;

      nextOrder.splice(fromIndex, 1);
      nextOrder.splice(toIndex, 0, fromId);
      return { ...prev, order: nextOrder };
    });
  }, []);

  return (
    <div className={styles.browserPage}>
      <div className={styles.header}>
        <BrowserHeader
          key={Key}
          handleClickQuery={handleClickQuery}
          handleClickRefresh={handleClickRefresh}
          columnOptions={columnOptions}
          onChangeColumns={handleChangeColumns}
        />
      </div>

      <div className={styles.browser}>
        <BrowserList
          key={Key}
          items={studyData}
          loading={isLoading}
          refetch={refetch}
          columns={visibleColumns}
          onMoveColumn={moveColumn}
          handleClickRefresh={handleClickRefreshKeepPage}
        />
      </div>

      {isColumnsMenuOpen && (
        <SimpleContextMenu
          checkbox
          items={columnMenuItems}
          onClose={() => setIsColumnsMenuOpen(false)}
        />
      )}

      <div className={styles.footer}>
        <BrowserFooter
          key={Key}
          totalItems={Number(study?.count ?? 0)}
          currentPage={StudyViewState.currentPage}
          totalPages={totalPages}
        />
      </div>
    </div>
  );
};

export default BrowserPage;
